import { useState } from 'react';
import { useNavigate } from 'react-router';
import { KpiCard, Card, Badge, SlidePanel, Modal, toast, ViewingResultModal } from '@/components';
import { PanelSection, SpecGrid, SpecCell } from '@/components/SlidePanel';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './Dashboard.module.css';

interface ReportSummary {
  propertyCount: number; vacantCount: number; customerCount: number;
  contractedCount: number; contractCount: number; signedCount: number;
  thisMonthSigned: number; lastMonthSigned: number; monthDelta: number;
  portalStats: { source: string; cnt: string }[]; planPrice: number;
  commissionTotal: number; commissionTarget: number; commissionRatio: number;
}
interface Customer {
  id: string; name: string; phone: string; email: string;
  property_interest: string; source: string; status: string;
  assignee?: string; created_at: string; updated_at?: string;
}
interface Contract {
  id: string; contract_type: string; title: string;
  customer_name: string; customer_id?: string; status: string;
  created_at: string; property_id?: string; amount?: number;
}
interface QueueItem {
  id: string; property_name: string; property_id?: string;
  portal: string; status: string; error_message: string | null;
  submitted_at: string | null;
}
interface Property {
  id: string; name: string; address: string; layout: string;
  area: number; rent: number; status: string;
  portal_suumo: string; portal_homes: string; portal_athome: string;
  nearest_station?: string; walk_minutes?: number; built_year?: number;
  owner_name?: string;
}

const activityIconBg: Record<string, string> = {
  inquiry: '#eaf1fb', interested: '#eaf1fb', viewing: '#eaf1fb', applied_customer: '#eaf1fb',
  contract: '#e7f4ed', contracted: '#e7f4ed', payment: '#e7f4ed',
  portal: '#fef2e0',
  portal_error: '#fcecea',
};

const pipelineStages = [
  { key: 'new', label: '新規', color: '#3b82f6' },
  { key: 'interested', label: '連絡中', color: '#8b5cf6' },
  { key: 'viewing', label: '内見予定', color: '#f59e0b' },
  { key: 'applied', label: '申込中', color: '#10b981' },
  { key: 'contracted', label: '成約', color: '#059669' },
];

type PanelType = 'contract' | 'property' | 'viewing' | 'portal_error' | 'rent_payment' | null;

export function Dashboard() {
  const navigate = useNavigate();
  const { data: summary, loading: summaryLoading } = useApi<ReportSummary>('/tenant/reports/summary');
  const { data: customers, refetch: refetchCustomers } = useApi<Customer[]>('/tenant/customers');
  const { data: contracts } = useApi<Contract[]>('/tenant/contracts');
  const { data: portalQueue } = useApi<QueueItem[]>('/tenant/portals/queue');
  const { data: properties } = useApi<Property[]>('/tenant/properties');
  const { data: myTodos } = useApi<{
    stale_contacts: Array<{ id: string; name: string; last: string; days: number }>;
    expiring_contracts: Array<{ id: string; title: string; customer_name: string; contract_end: string; days: number }>;
  }>('/tenant/users/me/todos');

  const [panelType, setPanelType] = useState<PanelType>(null);
  const [panelData, setPanelData] = useState<ActivityItem | null>(null);
  const [showViewingForm, setShowViewingForm] = useState(false);
  const [viewingForm, setViewingForm] = useState({ customerId: '', propertyId: '', date: '', time: '', meetingPlace: '', note: '', assigneeId: '' });
  const [editingViewingId, setEditingViewingId] = useState<string | null>(null);
  // テナントのスタッフ一覧 (担当者セレクト用)
  const { data: tenantUsers } = useApi<Array<{ id: string; name: string }>>('/tenant/users');
  // 内見結果登録モーダル用
  const [resultTarget, setResultTarget] = useState<{ id: string; customerName: string } | null>(null);
  // Phase 1: 実データの内見予定 + スコープフィルタ (A+C+F)
  const authUser = useAuthStore(s => s.user);
  const [viewingScope, setViewingScope] = useState<'all' | 'mine' | 'unassigned'>(
    authUser?.role === 'tenant_staff' ? 'mine' : 'all'
  );
  const viewingApiPath = viewingScope === 'mine'
    ? '/tenant/customer-viewings?scope=mine'
    : '/tenant/customer-viewings?scope=upcoming';
  const { data: viewings, refetch: refetchViewings } = useApi<Array<{
    id: string;
    customer_id: string;
    customer_name: string;
    customer_email: string | null;
    property_id: string | null;
    property_name: string | null;
    property_address: string | null;
    scheduled_at: string;
    duration_minutes: number;
    assignee_id: string | null;
    meeting_place: string | null;
    status: string;
    note: string | null;
    reminded_at: string | null;
  }>>(viewingApiPath);
  const filteredViewings = viewingScope === 'unassigned'
    ? (viewings ?? []).filter(v => !v.assignee_id)
    : (viewings ?? []);

  const activeInquiries = (customers ?? []).filter(c => ['new', 'interested', 'viewing'].includes(c.status));
  // 実データ: 本日の内見件数 (KPI 用) — フィルタ前の全件から今日のみ抽出
  const todayViewings = (viewings ?? []).filter(v => {
    const d = new Date(v.scheduled_at);
    const n = new Date();
    return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  });
  const viewingCount = todayViewings.length;
  const signedCount = summary?.thisMonthSigned ?? 0;
  // 実績仲介手数料（APIから取得）
  const commissionTotal = summary?.commissionTotal ?? 0;
  const commissionTarget = summary?.commissionTarget ?? 500000;
  const commissionRatio = summary?.commissionRatio ?? 0;
  const monthDelta = summary?.monthDelta ?? 0;
  const deltaStr = monthDelta > 0 ? `↑ 前月比 +${monthDelta}%` : monthDelta < 0 ? `↓ 前月比 ${monthDelta}%` : '前月と同じ';

  type Kpi = { label: string; value: string; delta: string; trend: 'up' | 'down' | 'flat'; onClick?: () => void; linkLabel?: string };
  const kpis: Kpi[] = summary ? [
    { label: '今月成約件数', value: String(signedCount), delta: deltaStr, trend: monthDelta >= 0 ? 'up' : 'down' },
    { label: '管理物件数', value: String(summary.propertyCount), delta: `空室 ${summary.vacantCount}件`, trend: 'flat' },
    {
      label: '本日の内見',
      value: `${viewingCount}件`,
      delta: viewingCount > 0 ? '未完了' : '予定なし',
      trend: 'flat',
      onClick: () => setPanelType('viewing'),
      linkLabel: '📅 内見スケジュール →',
    },
    { label: '今月の仲介手数料', value: `¥${commissionTotal.toLocaleString()}`, delta: commissionRatio > 0 ? `${commissionRatio >= 100 ? '↑' : '↓'} 目標比 ${commissionRatio}%` : '', trend: commissionRatio >= 100 ? 'up' : commissionRatio > 0 ? 'down' : 'flat' },
  ] : [];

  if (summaryLoading) return <div className={styles.page}>読み込み中...</div>;

  const pipelineData = pipelineStages.map(stage => ({ ...stage, customers: (customers ?? []).filter(c => c.status === stage.key) }));
  const activities = buildActivities(customers ?? [], contracts ?? [], portalQueue ?? []);

  const formatTime = (d: string) => {
    const date = new Date(d); const now = new Date();
    const diffMs = now.getTime() - date.getTime(); const diffHours = diffMs / 3600000;
    if (diffHours < 1) return `${Math.max(1, Math.floor(diffMs / 60000))}分前`;
    if (diffHours < 24) return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    if (diffHours < 48) return `昨日 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };
  const fmtDate = (d: string) => { const dt = new Date(d); return `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}`; };

  const handleActivityClick = (a: ActivityItem) => {
    switch (a.type) {
      case 'inquiry': case 'interested': case 'contracted': case 'applied_customer':
        if (a.customerId) navigate(`/crm?id=${a.customerId}`); break;
      case 'viewing': setPanelType('viewing'); setPanelData(a); break;
      case 'contract': setPanelType('contract'); setPanelData(a); break;
      case 'portal': setPanelType('property'); setPanelData(a); break;
      case 'portal_error': setPanelType('portal_error'); setPanelData(a); break;
      case 'payment': setPanelType('rent_payment'); setPanelData(a); break;
    }
  };
  const closePanel = () => { setPanelType(null); setPanelData(null); };

  const panelContract = panelData?.contractId ? (contracts ?? []).find(c => c.id === panelData.contractId) : null;
  const panelProperty = panelData?.propertyName ? (properties ?? []).find(p => p.name === panelData.propertyName) : null;
  const viewingCustomers = (customers ?? []).filter(c => c.status === 'viewing'); // 互換用 (他パネルで使用)
  const errorQueueItems = panelData?.portalName
    ? (portalQueue ?? []).filter(q => q.status === 'error' && q.portal === panelData.portalName)
    : (portalQueue ?? []).filter(q => q.status === 'error');

  // 内見予約の登録 or 編集 (customer_viewings に日時付きで実データ保存)
  const handleSubmitViewing = async () => {
    if (!editingViewingId && !viewingForm.customerId) { toast('顧客を選択してください', 'error'); return; }
    if (!viewingForm.date || !viewingForm.time) { toast('日付と時刻を指定してください', 'error'); return; }
    const scheduledAt = new Date(`${viewingForm.date}T${viewingForm.time}:00`).toISOString();
    try {
      if (editingViewingId) {
        // 編集
        await api(`/tenant/customer-viewings/${editingViewingId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            property_id: viewingForm.propertyId || undefined,
            scheduled_at: scheduledAt,
            assignee_id: viewingForm.assigneeId || undefined,
            meeting_place: viewingForm.meetingPlace || undefined,
            note: viewingForm.note || undefined,
          }),
        });
        toast('内見予約を更新しました', 'success');
      } else {
        // 新規
        await api('/tenant/customer-viewings', {
          method: 'POST',
          body: JSON.stringify({
            customer_id: viewingForm.customerId,
            property_id: viewingForm.propertyId || undefined,
            scheduled_at: scheduledAt,
            assignee_id: viewingForm.assigneeId || undefined,
            meeting_place: viewingForm.meetingPlace || undefined,
            note: viewingForm.note || undefined,
          }),
        });
        // 顧客ステータスも 'viewing' に更新 (既存互換のため)
        try {
          await api(`/tenant/customers/${viewingForm.customerId}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'viewing' }),
          });
        } catch { /* noop */ }
        toast('内見予約を登録しました', 'success');
      }
      setShowViewingForm(false);
      setEditingViewingId(null);
      setViewingForm({ customerId: '', propertyId: '', date: '', time: '', meetingPlace: '', note: '', assigneeId: '' });
      refetchViewings();
      refetchCustomers();
    } catch (e) {
      toast((e as Error)?.message || '保存に失敗しました', 'error');
    }
  };
  // 編集モード起動ヘルパー
  const openEditViewing = (v: { id: string; customer_id: string; property_id: string | null; scheduled_at: string; assignee_id: string | null; meeting_place: string | null; note: string | null }) => {
    const d = new Date(v.scheduled_at);
    const pad = (n: number) => String(n).padStart(2, '0');
    setViewingForm({
      customerId: v.customer_id,
      propertyId: v.property_id || '',
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      meetingPlace: v.meeting_place || '',
      note: v.note || '',
      assigneeId: v.assignee_id || '',
    });
    setEditingViewingId(v.id);
    setShowViewingForm(true);
  };

  // 家賃CSV出力
  const handleRentCsv = () => {
    const allProps = properties ?? [];
    const headers = ['物件名', '住所', '賃料', 'ステータス', 'オーナー'];
    const rows = allProps.map(p => [p.name, p.address, p.rent, p.status === 'occupied' ? '入金済' : '未入金', p.owner_name || '']);
    const bom = '\uFEFF';
    const csv = bom + [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `家賃入金確認_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast('CSVファイルをダウンロードしました', 'success');
  };

  // 一括督促
  const handleBulkRemind = async () => {
    const vacant = (properties ?? []).filter(p => p.status === 'vacant');
    if (vacant.length === 0) { toast('未入金物件はありません', 'info'); return; }
    try {
      const items = vacant.map(p => ({ email: '', name: p.owner_name || '入居者', propertyName: p.name, amount: p.rent }));
      await api('/tenant/rent/bulk-remind', { method: 'POST', body: JSON.stringify({ items }) });
      toast(`${vacant.length}件の未入金者へ一括督促メールを送信しました`, 'success');
    } catch { toast('一括督促に失敗しました', 'error'); }
  };

  const now = new Date();
  const monthLabel = `${now.getFullYear()}年${now.getMonth() + 1}月`;

  // 非viewingの顧客リスト（内見予約用）
  // 全顧客 (予約モーダル select 用、ステータス不問)

  // 今日のTODO件数
  const todoCount = (myTodos?.stale_contacts.length ?? 0) + (myTodos?.expiring_contracts.length ?? 0);

  return (
    <div className={styles.page}>
      {/* 今日のTODO */}
      {todoCount > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%)',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#78350f' }}>
            <span style={{ fontSize: 16 }}>⏰</span>
            <span>今日の TODO（{todoCount}件）</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(myTodos?.stale_contacts ?? []).slice(0, 5).map(c => (
              <button
                key={`stale-${c.id}`}
                onClick={() => navigate(`/crm?id=${c.id}`)}
                style={{
                  background: '#fff', border: '1px solid #f59e0b', borderRadius: 4,
                  padding: '4px 10px', fontSize: 11, color: '#78350f', cursor: 'pointer',
                }}
                title={`最終対応: ${new Date(c.last).toLocaleDateString('ja-JP')}`}>
                💬 {c.days}日未連絡: {c.name}様
              </button>
            ))}
            {(myTodos?.stale_contacts.length ?? 0) > 5 && (
              <span style={{ fontSize: 11, color: '#78350f', alignSelf: 'center' }}>
                …ほか {(myTodos?.stale_contacts.length ?? 0) - 5} 件
              </span>
            )}
            {(myTodos?.expiring_contracts ?? []).slice(0, 5).map(c => (
              <button
                key={`exp-${c.id}`}
                onClick={() => navigate('/contracts')}
                style={{
                  background: '#fff', border: '1px solid #dc2626', borderRadius: 4,
                  padding: '4px 10px', fontSize: 11, color: '#7f1d1d', cursor: 'pointer',
                }}
                title={`期限: ${new Date(c.contract_end).toLocaleDateString('ja-JP')}`}>
                📅 契約期限{c.days}日前: {c.customer_name || c.title}
              </button>
            ))}
            {(myTodos?.expiring_contracts.length ?? 0) > 5 && (
              <span style={{ fontSize: 11, color: '#7f1d1d', alignSelf: 'center' }}>
                …ほか {(myTodos?.expiring_contracts.length ?? 0) - 5} 件
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <div className={styles.grid2}>
        <Card title="🎯 問い合わせパイプライン" actions={<a className={styles.cardLink} onClick={() => navigate('/crm')}>全件表示 →</a>}>
          <div className={styles.pipeline}>
            {pipelineData.map(stage => (
              <div key={stage.key} className={styles.pipelineCol}>
                <div className={styles.pipelineHeader}>
                  <span className={styles.pipelineDot} style={{ background: stage.color }} />
                  <span className={styles.pipelineLabel}>{stage.label}</span>
                  <span className={styles.pipelineCount}>{stage.customers.length}</span>
                </div>
                <div className={styles.pipelineCards}>
                  {stage.customers.slice(0, 3).map(c => (
                    <div key={c.id} className={styles.pipelineCard} onClick={() => navigate(`/crm?id=${c.id}`)}>
                      {c.property_interest && <div className={styles.pipelineProperty}>{c.property_interest}</div>}
                      <div className={styles.pipelineCardName}>{c.name} 様</div>
                      <div className={styles.pipelineCardMeta}>
                        {c.source && <Badge variant="gray">{c.source}</Badge>}
                        <span>{formatTime(c.created_at)}</span>
                      </div>
                    </div>
                  ))}
                  {stage.customers.length === 0 && <div className={styles.pipelineEmpty}>案件なし</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="🔔 最新アクティビティ">
          <div className={styles.activityList}>
            {activities.slice(0, 12).map((a, i) => (
              <div key={i} className={styles.activityItem} onClick={() => handleActivityClick(a)}>
                <span className={styles.activityIcon} style={{ background: activityIconBg[a.type] ?? 'var(--surface2)' }}>{a.icon}</span>
                <div className={styles.activityContent}><span className={styles.activityText}>{a.text}</span></div>
                <span className={styles.activityTime}>{formatTime(a.time)}</span>
              </div>
            ))}
            {activities.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', textAlign: 'center' }}>アクティビティなし</div>}
          </div>
        </Card>
      </div>

      {/* ===== 契約詳細パネル ===== */}
      <SlidePanel open={panelType === 'contract'} onClose={closePanel}
        title={panelContract ? `${panelContract.title} — ${panelContract.customer_name}様` : '契約詳細'} width={580}
        actions={<div style={{ display: 'flex', gap: 6 }}>
          <button className={styles.panelBtn} onClick={() => {
            if (!panelContract) return;
            window.open(`/api/tenant/contracts/${panelContract.id}/pdf`, '_blank');
          }}>📥 PDF</button>
          <button className={styles.panelBtnPrimary} onClick={async () => {
            if (!panelContract) return;
            try {
              await api(`/tenant/contracts/${panelContract.id}/resend`, { method: 'POST' });
              toast('電子署名リンクを再送しました', 'success');
            } catch (e) { toast(e instanceof Error ? e.message : '送信に失敗しました', 'error'); }
          }}>📧 再送信</button>
        </div>}>
        {panelContract && (() => {
          const ct = panelContract;
          const statusIdx = ['draft', 'pending', 'sent', 'signed'].indexOf(ct.status);
          const steps = ['書類生成', '貸主署名', '借主署名', '宅建士確認', '完了'];
          const stepDates = [fmtDate(ct.created_at), '', '', '', ''];
          if (ct.status === 'signed') steps.forEach((_, i) => { stepDates[i] = stepDates[i] || fmtDate(ct.created_at); });
          const prop = ct.property_id ? (properties ?? []).find(p => p.id === ct.property_id) : (properties ?? [])[0];
          return <>
            <PanelSection title="電子署名フロー">
              <div className={styles.signFlow}>
                {steps.map((step, i) => {
                  const done = ct.status === 'signed' || i <= statusIdx;
                  return (
                    <div key={step} className={`${styles.signStep} ${done ? styles.signDone : ''}`}>
                      <div className={styles.signDot}>{done ? '✓' : (i + 1)}</div>
                      <div className={styles.signLabel}>{step}</div>
                      {stepDates[i] && <div className={styles.signDate}>{stepDates[i]}</div>}
                    </div>
                  );
                })}
              </div>
              {ct.status === 'signed' && (
                <div className={styles.signSuccess}><span>✅</span><div><div style={{ fontWeight: 600, color: 'var(--green)' }}>電子署名が完了しました</div><div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 2 }}>{new Date(ct.created_at).toLocaleString('ja-JP')} — 全署名者が完了</div></div></div>
              )}
            </PanelSection>
            <PanelSection title="契約概要">
              <SpecGrid>
                <SpecCell label="借主" value={ct.customer_name} />
                <SpecCell label="貸主" value={prop?.owner_name || '—'} />
                <SpecCell label="物件" value={prop ? prop.address : '—'} />
                <SpecCell label="間取り" value={prop ? `${prop.layout} / ${prop.area}m²` : '—'} />
                <SpecCell label="賃料" value={prop ? `¥${prop.rent?.toLocaleString()}/月` : '—'} mono />
                <SpecCell label="作成日" value={new Date(ct.created_at).toLocaleDateString('ja-JP')} mono />
              </SpecGrid>
            </PanelSection>
            <PanelSection title="AI 自動入力プレビュー">
              <div className={styles.aiPreview}>
                <div className={styles.aiPreviewTitle}>賃貸借契約書</div>
                {[['契約日', new Date(ct.created_at).toLocaleDateString('ja-JP', { era: 'long' })], ['賃貸人', prop?.owner_name || '—'], ['賃借人', ct.customer_name], ['物件所在地', prop ? `東京都${prop.address}` : '—']].map(([lbl, val]) => (
                  <div key={lbl as string} className={styles.aiRow}><span className={styles.aiLabel}>{lbl}</span><span className={styles.aiValue}>{val}<span className={styles.aiAuto}>AUTO</span></span></div>
                ))}
                <div className={styles.aiNote}>🤖 顧客・物件データから AUTO 項目を自動入力しました。内容を確認してください。</div>
              </div>
            </PanelSection>
          </>;
        })()}
      </SlidePanel>

      {/* ===== 内見スケジュールパネル (customer_viewings 実データ + グルーピング) ===== */}
      <SlidePanel open={panelType === 'viewing'} onClose={closePanel} title="内見スケジュール" width={560}
        actions={<button className={styles.panelBtnPrimary} onClick={() => setShowViewingForm(true)}>＋ 内見予約</button>}>
        {/* スコープセグメント (tenant_staff は mine 固定、非表示) */}
        {authUser?.role !== 'tenant_staff' && (
          <div className={styles.viewScopeSeg}>
            {(['all', 'mine', 'unassigned'] as const).map(s => (
              <button
                key={s}
                type="button"
                className={`${styles.viewScopeBtn} ${viewingScope === s ? styles.viewScopeActive : ''}`}
                onClick={() => setViewingScope(s)}
              >
                {s === 'all' ? '全社' : s === 'mine' ? '自分の担当' : '未割当'}
              </button>
            ))}
          </div>
        )}
        {(() => {
          // 日付グルーピング
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrowStart = new Date(todayStart.getTime() + 86400000);
          const dayAfterTomorrow = new Date(todayStart.getTime() + 2 * 86400000);
          const weekEnd = new Date(todayStart.getTime() + 7 * 86400000);
          const groups: { key: 'today' | 'tomorrow' | 'thisWeek' | 'later'; label: string; color: string; items: typeof filteredViewings }[] = [
            { key: 'today', label: '🔴 今日', color: 'today', items: [] },
            { key: 'tomorrow', label: '🟡 明日', color: 'tomorrow', items: [] },
            { key: 'thisWeek', label: '⚪ 今週', color: 'thisWeek', items: [] },
            { key: 'later', label: '⚫ それ以降', color: 'later', items: [] },
          ];
          for (const v of filteredViewings) {
            const d = new Date(v.scheduled_at);
            if (d < todayStart) continue;
            if (d < tomorrowStart) groups[0].items.push(v);
            else if (d < dayAfterTomorrow) groups[1].items.push(v);
            else if (d < weekEnd) groups[2].items.push(v);
            else groups[3].items.push(v);
          }
          const total = groups.reduce((s, g) => s + g.items.length, 0);
          if (total === 0) {
            return <PanelSection title={`今後の予定（0件）`}><div style={{ padding: 16, color: 'var(--ink4)', textAlign: 'center' }}>予定なし</div></PanelSection>;
          }
          return groups.filter(g => g.items.length > 0).map(g => (
            <PanelSection key={g.key} title={<span className={`${styles.viewGroupHeader} ${styles['viewGroup_' + g.color]}`}>{g.label} <span className={styles.viewGroupCount}>({g.items.length}件)</span></span>}>
              {g.items.map(v => {
                const d = new Date(v.scheduled_at);
                const diffMs = d.getTime() - Date.now();
                const showCountdown = g.key === 'today' && diffMs > 0 && diffMs < 2 * 3600 * 1000;
                const mins = Math.max(0, Math.floor(diffMs / 60000));
                const countdownText = showCountdown ? `あと${Math.floor(mins / 60) > 0 ? `${Math.floor(mins / 60)}h` : ''}${mins % 60}m` : '';
                return (
                  <div key={v.id} className={`${styles.viewItem} ${g.key === 'today' ? styles.viewItemToday : ''}`}>
                    <div className={styles.viewTime}>
                      <div className={styles.viewDateTop}>{d.getMonth() + 1}/{d.getDate()}</div>
                      <div className={styles.viewDateBot}>{String(d.getHours()).padStart(2, '0')}:{String(d.getMinutes()).padStart(2, '0')}</div>
                      {countdownText && <div className={styles.viewCountdown}>{countdownText}</div>}
                    </div>
                    <div className={styles.viewInfo} style={{ cursor: 'pointer' }} onClick={() => { closePanel(); navigate(`/crm?id=${v.customer_id}`); }} title="顧客詳細を開く">
                      <div className={styles.viewProp}>{v.property_name || '—'}</div>
                      <div className={styles.viewCust}>
                        {v.customer_name} 様{v.meeting_place ? ` ｜ ${v.meeting_place}` : ''}
                        {v.reminded_at && <span style={{ fontSize: 10, color: 'var(--ink3)', marginLeft: 6 }}>📧 送信済</span>}
                      </div>
                    </div>
                    <div className={styles.viewActions}>
                      <button className={styles.panelBtnIcon} onClick={async () => {
                        try {
                          await api(`/tenant/customer-viewings/${v.id}/remind`, { method: 'POST' });
                          toast(`${v.customer_name}様にリマインダーを送信しました`, 'success');
                          refetchViewings();
                        } catch (e) { toast((e as Error)?.message || '送信に失敗しました', 'error'); }
                      }} title="リマインダーメール送信">📧</button>
                      <button className={styles.panelBtnIcon} onClick={() => setResultTarget({ id: v.id, customerName: v.customer_name })} title="内見結果登録 (完了にする)">✅</button>
                      <button className={styles.panelBtnIcon} onClick={() => openEditViewing(v)} title="予約を編集">✏️</button>
                      <button className={`${styles.panelBtnIcon} ${styles.viewCancelBtn}`} onClick={async () => {
                        if (!confirm(`${v.customer_name}様の内見予定 (${d.toLocaleString('ja-JP')}) をキャンセルしますか?`)) return;
                        try {
                          await api(`/tenant/customer-viewings/${v.id}`, { method: 'DELETE' });
                          toast('キャンセルしました', 'success');
                          refetchViewings();
                        } catch (e) { toast((e as Error)?.message || 'キャンセルに失敗しました', 'error'); }
                      }} title="内見予定をキャンセル">🗑</button>
                    </div>
                  </div>
                );
              })}
            </PanelSection>
          ));
        })()}
      </SlidePanel>

      {/* ===== 内見予約モーダル ===== */}
      <Modal open={showViewingForm} onClose={() => { setShowViewingForm(false); setEditingViewingId(null); }} title={editingViewingId ? '内見予約の編集' : '内見予約登録'} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.panelBtn} onClick={() => { setShowViewingForm(false); setEditingViewingId(null); }}>キャンセル</button>
          <button className={styles.panelBtnPrimary} onClick={handleSubmitViewing}>{editingViewingId ? '更新' : '予約登録'}</button>
        </div>
      }>
        <div className={styles.formStack}>
          <div className={styles.formGroup}><label className={styles.formLabel}>顧客 *</label>
            <select className={styles.formInput} value={viewingForm.customerId} disabled={!!editingViewingId} onChange={e => setViewingForm({ ...viewingForm, customerId: e.target.value })}>
              <option value="">選択してください</option>
              {(customers ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>物件 (任意)</label>
            <select className={styles.formInput} value={viewingForm.propertyId} onChange={e => setViewingForm({ ...viewingForm, propertyId: e.target.value })}>
              <option value="">選択しない</option>
              {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className={styles.formRow2}>
            <div className={styles.formGroup}><label className={styles.formLabel}>日付 *</label>
              <input className={styles.formInput} type="date" value={viewingForm.date} onChange={e => setViewingForm({ ...viewingForm, date: e.target.value })} />
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>時間 *</label>
              <input className={styles.formInput} type="time" value={viewingForm.time} onChange={e => setViewingForm({ ...viewingForm, time: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>担当者 (任意 / 未指定は自分)</label>
            <select className={styles.formInput} value={viewingForm.assigneeId} onChange={e => setViewingForm({ ...viewingForm, assigneeId: e.target.value })}>
              <option value="">自分 ({authUser?.name || '未設定'})</option>
              {(tenantUsers ?? []).filter(u => u.id !== authUser?.id).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>集合場所</label>
            <input className={styles.formInput} value={viewingForm.meetingPlace} onChange={e => setViewingForm({ ...viewingForm, meetingPlace: e.target.value })} placeholder="現地 / 渋谷駅ハチ公前 / 弊社事務所 など" />
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>メモ</label>
            <textarea className={styles.formInput} value={viewingForm.note} onChange={e => setViewingForm({ ...viewingForm, note: e.target.value })} rows={2} placeholder="顧客からの要望・注意点など" />
          </div>
        </div>
      </Modal>

      {/* ===== ポータルエラー詳細パネル ===== */}
      <SlidePanel open={panelType === 'portal_error'} onClose={closePanel}
        title={`入稿エラー詳細 — ${panelData?.portalName || ''}`} width={560}
        actions={<div style={{ display: 'flex', gap: 6 }}>
          <button className={styles.panelBtn} onClick={() => toast('エラーログをコピーしました', 'info')}>📋 ログ</button>
          <button className={styles.panelBtnPrimary} onClick={() => { toast('修正して再入稿しました', 'success'); closePanel(); }}>🔁 再入稿</button>
        </div>}>
        <PanelSection>
          <div className={styles.errorBanner}><span style={{ fontSize: 18 }}>⚠️</span><div><div style={{ fontWeight: 700, color: 'var(--red)' }}>入稿エラーが発生しています</div><div style={{ fontSize: '12.5px', color: 'var(--ink2)', marginTop: 2 }}>{panelData?.portalName || ''} APIへの入稿で{errorQueueItems.length}件のエラーが発生しました。要因確認後、修正して再入稿してください。</div></div></div>
          <SpecGrid><SpecCell label="発生日時" value={panelData?.time ? new Date(panelData.time).toLocaleString('ja-JP') : '—'} mono /><SpecCell label="エラー件数" value={`${errorQueueItems.length}件`} /></SpecGrid>
        </PanelSection>
        <PanelSection title="エラー物件">
          {errorQueueItems.map(q => (
            <div key={q.id} className={styles.errorCard}>
              <div className={styles.errorCardHeader}><span style={{ fontWeight: 600 }}>{q.property_name}</span><Badge variant="red">エラー</Badge></div>
              <div className={styles.errorMessage}>{q.error_message || 'ERROR: 入稿データに不備があります。'}</div>
              <div className={styles.errorCardActions}>
                <button className={styles.panelBtn} onClick={() => { closePanel(); navigate('/properties'); }}>物件を編集</button>
                <button className={styles.panelBtnPrimary} onClick={() => toast('修正して再入稿しました', 'success')}>修正して再入稿</button>
              </div>
            </div>
          ))}
        </PanelSection>
      </SlidePanel>

      {/* ===== 物件詳細パネル ===== */}
      <SlidePanel open={panelType === 'property'} onClose={closePanel} title={panelProperty?.name || panelData?.propertyName || '物件詳細'} width={560}
        actions={<div style={{ display: 'flex', gap: 6 }}><button className={styles.panelBtn} onClick={() => { closePanel(); navigate('/properties'); }}>✏️ 編集</button><button className={styles.panelBtnPrimary} onClick={() => toast('全媒体に再入稿しました', 'success')}>📡 再入稿</button></div>}>
        {panelProperty && <>
          <PanelSection title="物件スペック"><SpecGrid>
            <SpecCell label="間取り" value={panelProperty.layout || '—'} /><SpecCell label="面積" value={panelProperty.area ? `${panelProperty.area}m²` : '—'} />
            <SpecCell label="築年数" value={panelProperty.built_year ? `${panelProperty.built_year}年` : '—'} /><SpecCell label="賃料" value={`¥${panelProperty.rent?.toLocaleString()}/月`} mono />
            <SpecCell label="最寄駅" value={panelProperty.nearest_station ? `${panelProperty.nearest_station} 徒歩${panelProperty.walk_minutes}分` : '—'} /><SpecCell label="住所" value={panelProperty.address} />
          </SpecGrid></PanelSection>
          <PanelSection title="ポータル入稿状況">
            {[['SUUMO', panelProperty.portal_suumo], ["HOME'S", panelProperty.portal_homes], ['athome', panelProperty.portal_athome]].map(([name, status]) => (
              <div key={name as string} className={styles.portalRow}><span style={{ fontWeight: 600 }}>{name}</span><Badge variant={status === 'submitted' ? 'green' : 'gray'}>{status === 'submitted' ? '掲載中' : '未入稿'}</Badge></div>
            ))}
          </PanelSection>
          <PanelSection title="担当者・オーナー"><SpecGrid><SpecCell label="オーナー" value={panelProperty.owner_name || '—'} /></SpecGrid></PanelSection>
        </>}
      </SlidePanel>

      {/* ===== 家賃入金パネル（モック準拠） ===== */}
      <SlidePanel open={panelType === 'rent_payment'} onClose={closePanel} title={`家賃入金確認（${monthLabel}）`} width={520}
        actions={<div style={{ display: 'flex', gap: 6 }}>
          <button className={styles.panelBtn} onClick={handleRentCsv}>📤 CSV</button>
          <button className={styles.panelBtnPrimary} onClick={handleBulkRemind}>📧 一括督促</button>
        </div>}>
        <PanelSection>
          <SpecGrid>
            <SpecCell label="管理物件数" value={`${summary?.propertyCount ?? 0}件`} mono />
            <SpecCell label="入金確認済" value={`${(summary?.propertyCount ?? 0) - (summary?.vacantCount ?? 0)}件`} mono />
            <SpecCell label="未入金" value={`${summary?.vacantCount ?? 0}件`} mono />
          </SpecGrid>
        </PanelSection>
        <PanelSection title={`未入金物件（${(properties ?? []).filter(p => p.status === 'vacant').length}件）`}>
          {(properties ?? []).filter(p => p.status === 'vacant').map((p, i) => (
            <div key={p.id} className={styles.rentItem}>
              <div className={styles.rentDot} style={{ background: i < 2 ? 'var(--red)' : 'var(--amber)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.address || p.name} — {p.owner_name || '入居者'}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--ink3)', marginTop: 2 }}>¥{p.rent?.toLocaleString()} ｜ {i < 2 ? `${i + 3}日滞納` : '支払予定連絡あり'}</div>
              </div>
              {i < 2 ? (
                <button className={styles.warnBtn} onClick={async () => {
                  try {
                    await api('/tenant/rent/bulk-remind', { method: 'POST', body: JSON.stringify({ items: [{ email: '', name: p.owner_name || '入居者', propertyName: p.name, amount: p.rent }] }) });
                    toast(`${p.name}の督促メールを送信しました`, 'success');
                  } catch { toast('送信に失敗しました', 'error'); }
                }}>督促</button>
              ) : (
                <button className={styles.panelBtn} onClick={() => toast('確認済にしました', 'success')}>確認済</button>
              )}
            </div>
          ))}
          {(properties ?? []).filter(p => p.status === 'vacant').length === 0 && <div style={{ padding: 16, color: 'var(--ink4)', textAlign: 'center' }}>未入金物件なし</div>}
        </PanelSection>
      </SlidePanel>

      {/* 内見結果登録モーダル (共通) */}
      <ViewingResultModal
        open={!!resultTarget}
        onClose={() => setResultTarget(null)}
        viewingId={resultTarget?.id ?? null}
        subTitle={resultTarget?.customerName}
        onSaved={() => { refetchViewings(); refetchCustomers(); }}
      />
    </div>
  );
}

interface ActivityItem {
  icon: string; text: string; time: string; type: string;
  customerId?: string; contractId?: string; propertyName?: string; portalName?: string;
}

function buildActivities(customers: Customer[], contracts: Contract[], portalQueue: QueueItem[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  customers.forEach(c => {
    const time = c.updated_at || c.created_at;
    if (c.status === 'new') items.push({ icon: '📨', text: `${c.name}様から問い合わせ${c.property_interest ? `（${c.property_interest}）` : ''}`, time: c.created_at, type: 'inquiry', customerId: c.id });
    if (c.status === 'interested' || c.status === 'contacted') items.push({ icon: '📞', text: `${c.name}様 連絡対応中`, time, type: 'interested', customerId: c.id });
    if (c.status === 'viewing') items.push({ icon: '👁️', text: `${c.name}様の内見予約確定`, time, type: 'viewing', customerId: c.id });
    if (c.status === 'applied') items.push({ icon: '📝', text: `${c.name}様 申込受付`, time, type: 'applied_customer', customerId: c.id });
    if (c.status === 'contracted') items.push({ icon: '✅', text: `${c.name}様 成約`, time, type: 'contracted', customerId: c.id });
  });
  (contracts ?? []).forEach(ct => {
    if (ct.status === 'signed') items.push({ icon: '✅', text: `${ct.customer_name}様の${ct.title} 電子署名完了`, time: ct.created_at, type: 'contract', contractId: ct.id });
    if (ct.status === 'sent') items.push({ icon: '📤', text: `${ct.customer_name}様へ${ct.title}を送付`, time: ct.created_at, type: 'contract', contractId: ct.id });
    if (ct.status === 'draft') items.push({ icon: '📝', text: `${ct.customer_name}様の${ct.title} 作成中`, time: ct.created_at, type: 'contract', contractId: ct.id });
  });
  portalQueue.forEach(q => {
    if ((q.status === 'submitted' || q.status === 'completed') && q.submitted_at) items.push({ icon: '🏠', text: `${q.property_name} を${q.portal}に入稿完了`, time: q.submitted_at, type: 'portal', propertyName: q.property_name });
    if (q.status === 'error' && q.submitted_at) items.push({ icon: '⚠️', text: `${q.portal} 入稿エラー — ${q.property_name}`, time: q.submitted_at, type: 'portal_error', propertyName: q.property_name, portalName: q.portal });
  });
  const now = new Date();
  const rentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 9, 30);
  items.push({ icon: '💰', text: `${now.getMonth() + 1}月分家賃入金確認完了`, time: rentDate.toISOString(), type: 'payment' });
  items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  return items;
}
